export interface Pagination {
    limit: number
    page: number
    from: number
    to: number
    total?: number
    isPaginated: boolean
    hasNext: boolean
    hasPrevious: boolean
    nextPage: () => void
    previousPage: () => void
    setTotal: (count?: number | undefined) => void
    reset: () => boolean
}

export function usePagination(_limit: number = 50, options: { syncQuery?: boolean } = {}) {
    const limit = _limit
    const page = ref<number>(0)
    const total = ref<number>()

    const from = computed(() => page.value * limit)
    const to = computed(() => from.value + limit - 1)

    const isPaginated = computed(() => !!total.value && total.value > limit)

    const hasNext = computed(() => !!total.value && total.value > to.value + 1)
    const hasPrevious = computed(() => page.value > 0)

    function nextPage() {
        if (hasNext.value) {
            page.value = page.value + 1
        }
    }

    function previousPage() {
        if (hasPrevious.value) {
            page.value = page.value - 1
        }
    }

    function setTotal(count?: number) {
        total.value = count
    }

    // Paging through results is history the user expects Back to walk; a reset forced by
    // a filter or saved-view change is not.
    let replaceNext = false

    function reset() {
        if (page.value === 0) {
            return false
        }

        replaceNext = true
        page.value = 0
        return true
    }

    if (options.syncQuery) {
        const route = useRoute()
        const router = useRouter()

        // ?page is 1-based and omitted on the first page; the internal index stays 0-based.
        const fromQuery = (value: unknown) => Math.max(0, (Number.parseInt(String(value ?? '')) || 1) - 1)

        // Back/forward already moved the URL; echoing a navigation back at the router
        // would fight the history stack.
        let syncingFromQuery = false

        page.value = fromQuery(route.query.page)

        watch(page, (val) => {
            if (syncingFromQuery) {
                syncingFromQuery = false
                return
            }

            const to = { query: { ...route.query, page: val === 0 ? undefined : String(val + 1) } }

            if (replaceNext) {
                replaceNext = false
                router.replace(to)
            }
            else {
                router.push(to)
            }
        })

        watch(() => route.query.page, (val) => {
            const next = fromQuery(val)

            if (next === page.value) {
                return
            }

            syncingFromQuery = true
            page.value = next
        })
    }

    return reactive({
        limit,
        page,
        from,
        to,
        total,
        isPaginated,
        hasNext,
        hasPrevious,
        nextPage,
        previousPage,
        setTotal,
        reset,
    })
}
